import { ButtonUpsellProtected } from '@components'

import { ButtonsGroup, useModal } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../../../../../enums/modalId'
import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useInvoiceEditorActions } from '../../../../contexts/invoiceEditorActionsContext'
import { useInvoiceEditor } from '../../../../contexts/invoiceEditorContext'
import { useInvoiceEditorForm } from '../../../../contexts/invoiceEditorFormContext'
import { useRecurringInvoiceForm } from '../../../../contexts/recurringInvoiceFormContext'
import { useSideActionsModals } from '../../../../contexts/sideActionsModalsContext'
import { InvoiceEditorView } from '../../../../enums/invoiceEditorView'
import { RecurringInvoiceEditorAction } from '../../../../enums/recurringInvoiceEditorAction'
import { useMissingComplianceDetails } from '../../../../hooks/missingComplianceDetails/useMissingComplianceDetails'
import { isActionModal } from '../../../../utils/sideActions/isActionModal'

export const RecurringInvoiceEditorActions = () => {
  const { t } = useTranslation()
  const { open: openLeavePreviewModal } = useModal(ModalId.InvoiceEditorLeavePreviewModal)
  const {
    openMissingComplianceDetailsModal,
    setMissingDetailsModalFinalizationAction,
    shouldSkipMissingComplianceDetailsModal,
    setApprovalModalFinalizationAction,
    modalActions,
  } = useSideActionsModals()
  const { isMissingComplianceDetails } = useMissingComplianceDetails()
  const { validate: validateInvoice } = useInvoiceEditorForm()
  const { validate: validateRecurringInvoiceSettings } = useRecurringInvoiceForm()
  const { isAppearanceSettingsDirtyRef, setView, view } = useInvoiceEditor()
  const {
    saveRecurringInvoiceDraft,
    isSavingRecurringInvoiceDraft,
    isLoading,
    saveAndActivateRecurringInvoice,
    isSavingAndActivatingRecurringInvoice,
  } = useInvoiceEditorActions()

  const recurringInvoiceActions = useMemo(
    () => ({
      [RecurringInvoiceEditorAction.SaveRecurringInvoiceDraft]: saveRecurringInvoiceDraft,
      [RecurringInvoiceEditorAction.SaveAndActivateRecurringInvoice]: saveAndActivateRecurringInvoice,
    }),
    [saveAndActivateRecurringInvoice, saveRecurringInvoiceDraft],
  )

  const finalizeRecurringInvoice = useCallback(
    (selectedAction: RecurringInvoiceEditorAction.SaveAndActivateRecurringInvoice) => {
      if (isActionModal(modalActions, selectedAction)) {
        setApprovalModalFinalizationAction(() => recurringInvoiceActions[selectedAction])
        modalActions[selectedAction]?.()
        return
      }

      recurringInvoiceActions[selectedAction]()
    },
    [modalActions, recurringInvoiceActions, setApprovalModalFinalizationAction],
  )

  const handleRecurringInvoiceAction = useCallback(
    async (selectedAction: RecurringInvoiceEditorAction) => {
      // we have two forms (invoice + recurring invoice settings) pretending to be one
      const isInvoiceFormValid = await validateInvoice()
      const isRecurringInvoiceFormValid = await validateRecurringInvoiceSettings()
      const areFormsValid = isInvoiceFormValid && isRecurringInvoiceFormValid

      if (!areFormsValid) {
        if (view === InvoiceEditorView.Preview) {
          if (isAppearanceSettingsDirtyRef.current) {
            openLeavePreviewModal()
          } else {
            setView(InvoiceEditorView.Editor)
          }
        }
        return
      }

      // no compliance/approvals needed for saving drafts
      if (selectedAction === RecurringInvoiceEditorAction.SaveRecurringInvoiceDraft) {
        recurringInvoiceActions[selectedAction]()
        return
      }

      // One modal collects missing compliance details for all supported markets:
      // FR uses it for all finalize options
      const shouldDisplayMissingComplianceDetailsModal =
        !shouldSkipMissingComplianceDetailsModal && isMissingComplianceDetails

      if (shouldDisplayMissingComplianceDetailsModal) {
        setMissingDetailsModalFinalizationAction(() => () => finalizeRecurringInvoice(selectedAction))
        openMissingComplianceDetailsModal()
        return
      }

      finalizeRecurringInvoice(selectedAction)
    },
    [
      finalizeRecurringInvoice,
      isAppearanceSettingsDirtyRef,
      isMissingComplianceDetails,
      openLeavePreviewModal,
      openMissingComplianceDetailsModal,
      recurringInvoiceActions,
      setMissingDetailsModalFinalizationAction,
      setView,
      shouldSkipMissingComplianceDetailsModal,
      validateInvoice,
      validateRecurringInvoiceSettings,
      view,
    ],
  )

  const handleSaveAsDraftButtonClick = useCallback(async () => {
    handleRecurringInvoiceAction(RecurringInvoiceEditorAction.SaveRecurringInvoiceDraft)
  }, [handleRecurringInvoiceAction])

  const handleSaveAndActivateButtonClick = useCallback(async () => {
    handleRecurringInvoiceAction(RecurringInvoiceEditorAction.SaveAndActivateRecurringInvoice)
  }, [handleRecurringInvoiceAction])

  return (
    <ButtonsGroup wrap={false}>
      <ButtonUpsellProtected
        disabled={isLoading || isSavingRecurringInvoiceDraft}
        loading={isSavingRecurringInvoiceDraft}
        onClick={handleSaveAsDraftButtonClick}
        trackingContext={TrackingContext.CreateRecurringInvoice}
        upsellScope={Scope.RecurringInvoiceUpsell}
        variant="outlined"
      >
        {t('external_invoices.editor.side_actions.recurring_invoice.save_draft')}
      </ButtonUpsellProtected>
      <ButtonUpsellProtected
        disabled={isLoading || isSavingAndActivatingRecurringInvoice}
        loading={isSavingAndActivatingRecurringInvoice}
        onClick={handleSaveAndActivateButtonClick}
        trackingContext={TrackingContext.CreateRecurringInvoice}
        upsellScope={Scope.RecurringInvoiceUpsell}
        variant="filled"
      >
        {t('external_invoices.editor.side_actions.recurring_invoice.save_and_activate')}
      </ButtonUpsellProtected>
    </ButtonsGroup>
  )
}
